import { inject, Injectable } from '@angular/core';
import { CommentsCountResponse, CommentsResponse, DeleteCommentResponse } from './models/comments.response';
import { KeyedPaginatedResponse } from '../../../_core/models/keyed-paginated.response';
import { Comment } from './models/comment.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CommentsFactory } from './comments.factory';
import { map, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CommentsService {
  private readonly http = inject(HttpClient);
  private readonly commentsFactory = inject(CommentsFactory);

  getCommentsByRecordId(recordId: string, lastEvaluatedKey: string): Observable<KeyedPaginatedResponse<Comment>> {
    const url = `${environment.apiUrl}/records/${recordId}/v2/comments`;
    const params: Record<string, string> = {};

    if (lastEvaluatedKey) {
      params.lastEvaluatedKey = lastEvaluatedKey;
    }

    return this.http.get<CommentsResponse>(url, { params }).pipe(
      map((response) => this.commentsFactory.buildCommentFromCommentResponse(response)),
    );
  }

  getCommentsByParentId(recordId: string, parentId: string, lastEvaluatedKey: string | null): Observable<KeyedPaginatedResponse<Comment>> {
    const url = `${environment.apiUrl}/records/${recordId}/v2/comments/${parentId}`;
    const params: Record<string, string> = {};

    if (lastEvaluatedKey) {
      params.lastEvaluatedKey = lastEvaluatedKey;
    }

    return this.http.get<CommentsResponse>(url, { params }).pipe(
      map((response) => this.commentsFactory.buildCommentFromCommentResponse(response)),
    );
  }

  getCommentsCount(recordId: string): Observable<number> {
    const url = `${environment.apiUrl}/records/${recordId}/v2/comments/count-approved`;

    return this.http.get<CommentsCountResponse>(url).pipe(
      map((response) => response.parentCommentCount)
    );
  }

  postComment(recordId: string, comment: string, userId: string): Observable<Comment> {
    const url = `${environment.apiUrl}/records/${recordId}/v2/comments`;

    return this.http.post<Comment>(url, { userId, comment });
  }

  postReply(recordId: string, comment: string, userId: string, parentId: string, rootCommentId: string, replyUserId: string): Observable<Comment> {
    const url = `${environment.apiUrl}/records/${recordId}/v2/comments`;

    return this.http.post<Comment>(url, { userId, comment, parentId, rootCommentId, replyUserId });
  }

  voteComment(recordId: string, commentId: string, userId: string, like: boolean): Observable<void> {
    const url = `${environment.apiUrl}/records/${recordId}/comments/${commentId}/vote`;
    const body = [
      `userid=${userId}`,
      `like=${like}`,
    ];

    return this.http.post<void>(url, body.join('&'), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      }),
    });
  }

  deleteComment(commentId: string): Observable<DeleteCommentResponse> {
    const url = `${environment.apiUrl}/records/v2/comments/${commentId}`;

    return this.http.delete<DeleteCommentResponse>(url);
  }
}
